import { useState, type MouseEvent } from 'react';
import { Check, X, ShieldCheck, ArrowLeft, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useSignupForm, type PasswordChecks } from '../../hooks/useSignupForm';
import type { IndustryType } from '@/lib/api/auth';
import logoEmblem from '@/components/images/logo-amblem.png';
import './SignupForm.scss';

// Curated list of widely-used IANA zones — one per UTC offset / region most
// of our users sign up from. Covers ~99% of business users without making
// them scroll through 400 entries. The auto-detected zone is prepended at
// runtime (with "(auto)" suffix) so the user can confirm at a glance. If
// the auto-detected zone happens to be unusual (e.g. "America/Argentina/
// Cordoba"), it's still selected — the curated list just gives them sane
// alternatives if they're signing up on behalf of a different region.
const COMMON_TIMEZONES: string[] = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

// Build the timezone dropdown options. If the browser reported a zone we
// don't carry in COMMON_TIMEZONES, hoist it to the top + suffix "(detected)"
// so it's the obvious default and the user knows we picked it for them.
const timezoneOptions = (detected: string) => {
  const labelFor = (tz: string) => `${tz}${tz === detected ? ' (detected)' : ''}`;
  if (!COMMON_TIMEZONES.includes(detected)) {
    return [{ value: detected, label: labelFor(detected) }, ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: labelFor(tz) }))];
  }
  return COMMON_TIMEZONES.map((tz) => ({ value: tz, label: labelFor(tz) }));
};

// Mirrors IndustryType enum from prowatchly_api/modules/auth/auth_schemas.py.
const INDUSTRIES: { value: IndustryType; label: string }[] = [
  { value: 'retail',         label: 'Retail' },
  { value: 'manufacturing',  label: 'Manufacturing' },
  { value: 'healthcare',     label: 'Healthcare' },
  { value: 'education',      label: 'Education' },
  { value: 'hospitality',    label: 'Hospitality' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'warehouse',      label: 'Warehouse' },
  { value: 'office',         label: 'Office' },
  { value: 'residential',    label: 'Residential' },
  { value: 'other',          label: 'Other' },
];

const PasswordHint = ({ ok, label }: { ok: boolean; label: string }) => (
  <li className={`signup-form__check ${ok ? 'is-ok' : ''}`}>
    {ok ? <Check size={11} /> : <X size={11} />}
    <span>{label}</span>
  </li>
);

const PasswordChecklist = ({ checks }: { checks: PasswordChecks }) => (
  <ul className="signup-form__checks">
    <PasswordHint ok={checks.length} label="At least 8 characters" />
    <PasswordHint ok={checks.upper}  label="One uppercase letter" />
    <PasswordHint ok={checks.lower}  label="One lowercase letter" />
    <PasswordHint ok={checks.digit}  label="One number" />
  </ul>
);

// 2-step pattern is what makes the no-scroll guarantee hold. 8 inputs in
// one card always overflows ≤768px laptops. Steps 5 → 3 fit any common
// viewport while keeping the form feeling lightweight, not bureaucratic.
type Step = 1 | 2;

export const SignupForm = () => {
  const f = useSignupForm();
  const [step, setStep] = useState<Step>(1);
  const [stepError, setStepError] = useState<string | null>(null);

  const step1Valid = (): string | null => {
    if (f.firstName.trim().length < 2) return 'First name must be at least 2 characters.';
    if (f.lastName.trim().length < 2)  return 'Last name must be at least 2 characters.';
    // Backend re-validates; this is just to keep the user from getting an
    // opaque error on step 2 from data they entered on step 1.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.workEmail.trim())) {
      return 'Enter a valid work email.';
    }
    if (f.company.trim().length < 2)   return 'Company name is required.';
    if (!f.industryType)               return 'Pick an industry to continue.';
    return null;
  };

  const onContinue = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const err = step1Valid();
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    setStep(2);
  };

  const onBack = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setStepError(null);
    setStep(1);
  };

  // The form-level error: prefer step-local error, fall back to backend error.
  const displayError = stepError || f.error;

  return (
    <form className="signup-form" onSubmit={f.onSubmit} noValidate>
      {/* Mobile-only emblem chip — same pattern as LoginForm. Desktop sees
          the emblem in the showcase column instead. */}
      <div className="signup-form__emblem-chip" aria-hidden="true">
        <span className="signup-form__emblem-halo" />
        <img src={logoEmblem} alt="" className="signup-form__emblem" />
      </div>

      <div className="signup-form__head">
        <span className="signup-form__kicker">PROVISION TENANT</span>
        <h2 className="signup-form__heading">Create your account</h2>
        <div className="signup-form__stepper" aria-label={`Step ${step} of 2`}>
          <span className={`signup-form__step ${step >= 1 ? 'is-active' : ''}`}>
            {step > 1 ? <Check size={11} strokeWidth={3} /> : '1'}
          </span>
          <span className="signup-form__step-line" aria-hidden="true">
            <span
              className="signup-form__step-line-fill"
              style={{ width: step >= 2 ? '100%' : '0%' }}
            />
          </span>
          <span className={`signup-form__step ${step >= 2 ? 'is-active' : ''}`}>2</span>
        </div>
        <p className="signup-form__step-label">
          {step === 1 ? 'Tell us about you' : 'Set a password and accept the terms'}
        </p>
      </div>

      {step === 1 && (
        <div className="signup-form__fields" key="step-1">
          <div className="signup-form__row">
            <Input
              label="First name"
              name="first_name"
              autoComplete="given-name"
              value={f.firstName}
              onChange={(e) => f.setFirstName(e.target.value)}
              required
              placeholder="John"
            />
            <Input
              label="Last name"
              name="last_name"
              autoComplete="family-name"
              value={f.lastName}
              onChange={(e) => f.setLastName(e.target.value)}
              required
              placeholder="Doe"
            />
          </div>

          <Input
            label="Work email"
            type="email"
            name="work_email"
            autoComplete="email"
            value={f.workEmail}
            onChange={(e) => f.setWorkEmail(e.target.value)}
            required
            placeholder="you@company.com"
          />

          <div className="signup-form__row">
            <Input
              label="Company"
              name="company"
              autoComplete="organization"
              value={f.company}
              onChange={(e) => f.setCompany(e.target.value)}
              required
              placeholder="Acme Corp"
            />

            <Select
              label="Industry"
              name="industry_type"
              id="industry_type"
              options={INDUSTRIES}
              value={f.industryType}
              onChange={(v) => f.setIndustryType(v as IndustryType)}
            />
          </div>

          {/* Timezone — auto-detected from the browser, override-able.
              The detected zone is prepended so the user can confirm at a
              glance. Used by the backend to render emails / reports in
              the user's local time and to default operator time pickers. */}
          <div className="signup-form__tz-row">
            <Globe size={12} className="signup-form__tz-icon" aria-hidden="true" />
            <Select
              label="Timezone"
              name="timezone"
              id="timezone"
              options={timezoneOptions(f.detectedTimezone)}
              value={f.timezone}
              onChange={f.setTimezone}
              searchable
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="signup-form__fields" key="step-2">
          <Input
            label="Password"
            type="password"
            name="password"
            autoComplete="new-password"
            value={f.password}
            onChange={(e) => f.setPassword(e.target.value)}
            required
            placeholder="••••••••"
          />

          {f.password.length > 0 && <PasswordChecklist checks={f.passwordChecks} />}

          <Input
            label="Confirm password"
            type="password"
            name="confirm_password"
            autoComplete="new-password"
            value={f.confirmPassword}
            onChange={(e) => f.setConfirmPassword(e.target.value)}
            required
            placeholder="••••••••"
          />

          <label className="signup-form__terms">
            <input
              type="checkbox"
              checked={f.termsAccepted}
              onChange={(e) => f.setTermsAccepted(e.target.checked)}
            />
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer">terms</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>.
            </span>
          </label>
        </div>
      )}

      {displayError && (
        <div className="signup-form__error" role="alert">
          {displayError}
        </div>
      )}

      <div className="signup-form__actions">
        {step === 1 && (
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={onContinue}
            className="signup-form__cta"
          >
            Continue
          </Button>
        )}
        {step === 2 && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={onBack}
              leftIcon={<ArrowLeft size={14} />}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={f.isLoading}
              className="signup-form__cta"
            >
              Create account
            </Button>
          </>
        )}
      </div>

      <div className="signup-form__trust" aria-hidden="true">
        <ShieldCheck size={12} />
        <span>TLS · Tenant-isolated · On-prem agent</span>
      </div>

      <p className="signup-form__alt">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </form>
  );
};
